/**
 * HistoryGroupItem - Linha da aba Histórico agrupada por (contato, canal)
 *
 * Cada linha representa a conversa FECHADA mais recente do par + contagem
 * total de atendimentos ("N conversas"). Visual espelhado no ConversationItem
 * (Avatar squircle + badge de canal via IntegrationIcon), sem badges de
 * não-lida/atendente (não fazem sentido em rollup de fechadas).
 */

import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import type { ConversationHistoryGroupResponse } from '@troia-v3/types';
import { formatRelativeDate, getInitials } from '../utils/dateFormatter';
import { IntegrationIcon } from '@/shared/icons';

interface HistoryGroupItemProps {
  group: ConversationHistoryGroupResponse;
  onClick?: () => void;
  expanded?: boolean;
  isSelected?: boolean;
}

export const HistoryGroupItem: React.FC<HistoryGroupItemProps> = ({
  group,
  onClick,
  expanded = true,
  isSelected = false,
}) => {
  const displayName = group.contact?.name || group.subject || `Conversa #${group.id.slice(0, 8)}`;
  const avatarUrl = group.contact?.picture;
  const providerId = group.provider?.type;

  return (
    <div
      data-testid={`history-group-item-${group.contactId}-${group.channelId}`}
      onClick={onClick}
      className={`border-b cursor-pointer transition-colors ${
        expanded ? `p-3 ${isSelected ? 'border-l-4' : ''}` : 'py-2 px-0 flex justify-center'
      }`}
      style={{
        borderColor: 'var(--theme-sidebar-border)',
        ...(isSelected ? { borderLeftColor: 'var(--theme-sidebar-active)' } : {}),
        backgroundColor: isSelected ? 'var(--theme-button-ghost-hover)' : 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div className={`flex ${expanded ? 'items-start gap-3' : 'items-center'}`}>
        {/* Avatar do contato + badge do canal (mesmo posicionamento do ConversationItem) */}
        <div className="relative flex-shrink-0">
          <Avatar className={expanded ? 'w-12 h-12' : 'w-10 h-10'} isSquircle>
            <AvatarImage src={avatarUrl} alt={displayName} isSquircle />
            <AvatarFallback isSquircle>
              <span className={`text-indigo-600 font-semibold ${expanded ? 'text-sm' : 'text-xs'}`}>
                {getInitials(displayName)}
              </span>
            </AvatarFallback>
          </Avatar>

          {/* Badge do canal - Expandida */}
          {providerId && expanded && (
            <div
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center overflow-hidden"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-sidebar-border)',
              }}
            >
              <IntegrationIcon id={providerId ?? 'unknown'} size={12} variant="brand" />
            </div>
          )}

          {/* Badge do canal - Recolhida */}
          {providerId && !expanded && (
            <div
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border overflow-hidden flex items-center justify-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-sidebar-border)',
              }}
            >
              <IntegrationIcon id={providerId ?? 'unknown'} size={8} variant="brand" />
            </div>
          )}
        </div>

        {/* Conteúdo - 2 linhas */}
        {expanded && (
          <div className="flex-1 min-w-0">
            {/* Linha 1: Nome + data da última conversa */}
            <div className="flex items-center justify-between mb-1">
              <h4
                className="font-semibold text-sm truncate"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {displayName}
              </h4>
              <span className="text-xs flex-shrink-0 ml-2" style={{ color: 'var(--theme-muted-text)' }}>
                {formatRelativeDate(group.lastMessageAt || group.createdAt)}
              </span>
            </div>

            {/* Linha 2: Preview da última mensagem + badge "N conversas" */}
            <div className="flex items-center gap-2">
              <p
                className="text-sm truncate flex-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {group.lastMessage || 'Sem mensagens'}
              </p>

              <span
                className="flex-shrink-0 h-5 px-1.5 text-xs font-medium rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--theme-button-ghost-hover)',
                  color: 'var(--theme-muted-text)',
                }}
                title={`${group.conversationCount} atendimento${group.conversationCount > 1 ? 's' : ''} neste canal`}
                data-testid={`history-group-count-${group.contactId}-${group.channelId}`}
              >
                {group.conversationCount} {group.conversationCount === 1 ? 'conversa' : 'conversas'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
